import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { showAlert, showConfirm } from "@erp/shared/utils/dialog";
import { parsePDFPrintOptions } from "./types";
import { copyStyleToNewWindowWithoutHover, resolveElement, openFullSizePopup, } from "./helpers";
/** html2canvas 스케일 — 고해상도 캡처용 */
const HTML2CANVAS_SCALE = 2;
/** 프리뷰 팝업 렌더링 대기 시간 (ms) */
const PREVIEW_RENDER_DELAY_MS = 500;
/** originPrintLogic 이미지 렌더링 대기 시간 (ms) */
const IMAGE_RENDER_DELAY_MS = 1000;
/** PDF 생성 후 resolve 대기 시간 (ms) */
const POST_PRINT_DELAY_MS = 500;
// ──────────────────────────────────────────────
// 유틸 헬퍼
// ──────────────────────────────────────────────
function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
/**
 * 캔버스를 PDF 페이지 높이에 맞게 분할하여 이미지를 추가한다.
 *
 * html2canvas로 캡처한 캔버스는 페이지보다 길 수 있으므로,
 * pdfHeightMinusMargin 단위로 잘라서 각 페이지에 삽입한다.
 */
function sliceCanvasToPdf({ pdf, canvas, imgWidth, pdfHeightMinusMargin, marginLeftRight, marginTopBottom, addPageBeforeFirst, }) {
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    // 페이지별 이미지 높이 계산
    const imgHeightPerPageArr = [];
    while (heightLeft > 0) {
        if (heightLeft < pdfHeightMinusMargin) {
            imgHeightPerPageArr.push(heightLeft);
        }
        else {
            imgHeightPerPageArr.push(pdfHeightMinusMargin);
        }
        heightLeft -= pdfHeightMinusMargin;
    }
    // 각 페이지 영역을 캔버스에서 잘라내어 JPEG 데이터로 변환
    const slicedImageDataArr = [];
    for (let i = 0; i < imgHeightPerPageArr.length; i++) {
        let prevPxHeight = 0;
        for (let ii = 0; ii < i; ii++) {
            prevPxHeight +=
                (imgHeightPerPageArr[ii] * canvas.width) / imgWidth;
        }
        const pxHeight = (imgHeightPerPageArr[i] * canvas.width) / imgWidth;
        const slicedCanvas = document.createElement("canvas");
        slicedCanvas.width = canvas.width;
        slicedCanvas.height = pxHeight;
        const ctx = slicedCanvas.getContext("2d");
        ctx.drawImage(canvas, 0, prevPxHeight, canvas.width, pxHeight, 0, 0, canvas.width, pxHeight);
        slicedImageDataArr.push(slicedCanvas.toDataURL("image/jpeg", 1.0));
    }
    // PDF에 페이지 추가 및 이미지 삽입
    slicedImageDataArr.forEach((imgData, index) => {
        if (addPageBeforeFirst || index > 0)
            pdf.addPage();
        pdf.addImage(imgData, "JPEG", marginLeftRight, marginTopBottom, imgWidth, imgHeightPerPageArr[index]);
    });
}
/**
 * 생성된 PDF를 blob URL로 변환하여 팝업 창에 표시한다.
 *
 * dataurlnewwindow 대신 blob URL을 사용하는 이유:
 * - setTimeout 콜백 안에서 window.open()을 호출하면 사용자 제스처 컨텍스트가
 *   만료되어 브라우저가 팝업을 차단하고 about:blank가 표시됨
 * - 미리 열어둔 팝업에 blob URL을 할당하면 이 문제를 회피할 수 있음
 *
 * 팝업이 닫히면 blob URL을 해제하여 메모리 누수를 방지한다.
 */
function displayPdfInPopup(pdf, popup) {
    const pdfBlobUrl = URL.createObjectURL(pdf.output("blob"));
    popup.location.href = pdfBlobUrl;
    // 팝업이 닫혔는지 주기적으로 확인 후 blob 해제
    const checkClosed = setInterval(() => {
        if (popup.closed) {
            clearInterval(checkClosed);
            URL.revokeObjectURL(pdfBlobUrl);
        }
    }, 1000);
}
/**
 * mode별 PDF 처리 분기
 */
function finalizePdf(pdf, popup, mode, fileName) {
    if (mode === "print") {
        pdf.autoPrint();
        displayPdfInPopup(pdf, popup);
    }
    else {
        pdf.save(fileName + ".pdf");
        setTimeout(() => popup.close(), POST_PRINT_DELAY_MS);
    }
}
// ──────────────────────────────────────────────
// 메인 통합 함수
// ──────────────────────────────────────────────
/**
 * HTML 요소를 PDF로 변환하여 인쇄 또는 다운로드한다.
 *
 * @param element - 대상 DOM 요소 또는 Vue 컴포넌트
 * @param mode - "print" (인쇄) 또는 "download" (PDF 다운로드)
 * @param paramObj - PDF 옵션
 */
export async function generatePdf(element, mode, paramObj) {
    const { fileName, countingTableIndex, rowCountPerPage, marginTopBottom, marginLeftRight, mmWidth, isLandscape, hasTotalRow, trTdClass, originPrintLogic, } = parsePDFPrintOptions(paramObj);
    const confirmMessage = mode === "print"
        ? "프린트를 위한 팝업창을 허용하시겠습니까?"
        : "PDF 파일 다운로드를 위한 팝업창을 허용하시겠습니까?";
    const errorLabel = mode === "print" ? "인쇄" : "PDF";
    if (!originPrintLogic) {
        try {
            const thisElement = resolveElement(element);
            let a4Width;
            let a4Height;
            let htmlBody = "";
            // 테이블에서 페이지별 행 분할 처리
            const tableList = thisElement.querySelectorAll("table");
            const countingTable = tableList[countingTableIndex < 0 ? tableList.length - 1 : countingTableIndex];
            const tbody = countingTable.querySelector("tbody");
            const trList = tbody.querySelectorAll('tr:not([style*="display: none"])');
            const trArray = Array.from(trList);
            let startIndex = 0;
            let endIndex = rowCountPerPage;
            const pageCount = Math.ceil(trArray.length / rowCountPerPage);
            // rowspan이 페이지 경계를 넘는 경우를 처리하기 위한 임시 배열
            const tmpRowspanArr = [];
            for (let i = 0; i < pageCount; i++) {
                const copiedElement = thisElement.cloneNode(true);
                const tables = copiedElement.querySelectorAll("table");
                const targetTable = tables[countingTableIndex < 0 ? tables.length - 1 : countingTableIndex];
                const tb = targetTable.querySelector("tbody");
                const trs = tb.querySelectorAll('tr:not([style*="display: none"])');
                const array = Array.from(trs);
                let zeroDiff = false;
                for (let ii = 0; ii < array.length; ii++) {
                    const node = array[ii];
                    // 합계 행이 있을 때 마지막 직전 페이지 처리
                    if (hasTotalRow &&
                        i === pageCount - 2 &&
                        endIndex === array.length - 1) {
                        endIndex -= 1;
                        zeroDiff = true;
                    }
                    if (ii < startIndex || ii >= endIndex) {
                        tb.removeChild(node);
                    }
                    else {
                        // 이전 페이지에서 넘어온 rowspan 셀을 현재 페이지 첫 행에 삽입
                        if (ii === startIndex) {
                            tmpRowspanArr.forEach((tmp) => {
                                const toInsertTd = tmp.node.cloneNode(true);
                                if (trs[ii].children.length >= tmp.cellIndex + 1) {
                                    trs[ii].insertBefore(toInsertTd, trs[ii].children[tmp.cellIndex]);
                                }
                                else {
                                    trs[ii].appendChild(toInsertTd);
                                }
                            });
                        }
                        // rowspan > 1인 셀을 기록하여 다음 페이지에서 재삽입할 수 있게 함
                        node.childNodes.forEach((child) => {
                            const el = child;
                            if (typeof el.style !== "undefined") {
                                if (el.style.display !== "none" &&
                                    !el.classList.contains("d-none")) {
                                    if (el.hasAttribute("rowspan") &&
                                        parseInt(el.getAttribute("rowspan")) > 1) {
                                        const tdObj = el.cloneNode(true);
                                        tmpRowspanArr.push({
                                            node: tdObj,
                                            cellIndex: el.cellIndex,
                                        });
                                    }
                                }
                            }
                        });
                        // rowspan 카운트 감소 — 0이 되면 배열에서 제거
                        for (let t = tmpRowspanArr.length - 1; t >= 0; t--) {
                            const tmp = tmpRowspanArr[t];
                            const tmpEl = tmp.node;
                            tmpEl.setAttribute("rowspan", String(parseInt(tmpEl.getAttribute("rowspan")) - 1));
                            if (parseInt(tmpEl.getAttribute("rowspan")) === 0) {
                                tmpRowspanArr.splice(t, 1);
                            }
                        }
                    }
                }
                // 빈 행 채우기 — 페이지당 행 수를 일정하게 유지
                const diff = zeroDiff
                    ? 0
                    : rowCountPerPage -
                        tb.querySelectorAll('tr:not([style*="display: none"])').length;
                if (diff > 0) {
                    for (let ii = 0; ii < diff; ii++) {
                        const tr = document.createElement("tr");
                        const td = document.createElement("td");
                        if (trTdClass) {
                            tr.classList.add(trTdClass);
                            td.classList.add(trTdClass);
                        }
                        td.innerText = "\u3000";
                        td.setAttribute("colspan", "100");
                        tr.appendChild(td);
                        if (hasTotalRow) {
                            tb.insertBefore(tr, tb.querySelector("tr:last-of-type"));
                        }
                        else {
                            tb.appendChild(tr);
                        }
                    }
                }
                htmlBody += `<div style="position: relative; margin-top: ${marginTopBottom}mm; margin-bottom: ${marginTopBottom}mm;">
                        ${copiedElement.outerHTML}
                      </div>`;
                startIndex = endIndex;
                endIndex += rowCountPerPage;
            }
            a4Width = `${mmWidth + marginLeftRight * 2}mm`;
            a4Height = `${pageCount *
                ((isLandscape ? mmWidth / 1.4141 : mmWidth * 1.4141) +
                    marginTopBottom * 2 * 2)}mm`;
            // 현재 페이지의 스타일시트를 복사 (hover 제거)
            const styleCopy = (await copyStyleToNewWindowWithoutHover()).replaceAll(`32pxborder-top-left-radius:0border-top-right-radius:0border-bottom-left-radius:0border-bottom-right-radius:0`, `32px; border-top-left-radius: 0; border-top-right-radius: 0; border-bottom-left-radius: 0; border-bottom-right-radius: 0;`);
            // 사용자에게 팝업 허용 확인
            if ((await showConfirm(confirmMessage)) === false) {
                return "팝업창 허용이 필요합니다.";
            }
            // 사용자 제스처 컨텍스트 내에서 팝업을 열어야 브라우저 차단을 피할 수 있음
            const previewPopup = openFullSizePopup();
            if (!previewPopup) {
                showAlert("팝업이 차단되었습니다. 브라우저 설정에서 팝업을 허용해주세요.", "팝업 차단");
                return "팝업이 차단되었습니다.";
            }
            // 프리뷰 HTML을 팝업에 렌더링 (로딩 오버레이로 가리고, 뒤에서 캡처)
            const previewContent = `<html><head><title>Print Preview</title><style>.d-none{display:none}${styleCopy}</style></head><body style="margin: 0px;"><div style="position:fixed;top:0;left:0;width:100vw;height:100vh;background:#fff;display:flex;align-items:center;justify-content:center;z-index:9999;font-size:18px;color:#666;">PDF 생성중...</div><div id="pdf-content">${htmlBody}</div></body></html>`;
            previewPopup.document.write(previewContent);
            previewPopup.document.childNodes[0].style.width =
                a4Width;
            previewPopup.document.childNodes[0].style.height =
                a4Height;
            // 프리뷰 DOM 렌더링 완료 대기 후 PDF 생성
            await delay(PREVIEW_RENDER_DELAY_MS);
            previewPopup.focus();
            // 이미지 렌더링 품질 최적화
            const imgTags = previewPopup.document.childNodes[0].querySelectorAll("img");
            imgTags.forEach((img) => {
                img.style.imageRendering = "optimizeQuality";
            });
            const pdf = new jsPDF(isLandscape ? "l" : "p", "mm", "a4");
            const pdfWidth = isLandscape ? 297 : 210;
            const pdfHeight = isLandscape ? 210 : 297;
            // 프리뷰 팝업의 #pdf-content 자식 요소들을 순회하며 PDF 페이지 생성
            const pdfContent = previewPopup.document.getElementById("pdf-content");
            const bodyChildren = pdfContent.childNodes;
            for (let d = 0; d < bodyChildren.length; d++) {
                const div = bodyChildren[d];
                const canvas = await html2canvas(div, { scale: HTML2CANVAS_SCALE });
                const imgWidth = pdfWidth - 2 * marginLeftRight;
                sliceCanvasToPdf({
                    pdf,
                    canvas,
                    imgWidth,
                    pdfHeightMinusMargin: pdfHeight,
                    marginLeftRight,
                    marginTopBottom,
                    addPageBeforeFirst: d > 0,
                });
                // 다중 페이지일 때 페이지 번호 표시
                if (bodyChildren.length > 1) {
                    const pageNumber = pdf.internal.getNumberOfPages();
                    pdf.setFontSize(10);
                    pdf.text(`${pageNumber}/${bodyChildren.length}`, pdf.internal.pageSize.getWidth() / 2, pdf.internal.pageSize.getHeight() - 5, { align: "center" });
                }
            }
            finalizePdf(pdf, previewPopup, mode, fileName);
            await delay(POST_PRINT_DELAY_MS);
        }
        catch (error) {
            console.error(`[${errorLabel} 오류]`, error);
            showAlert(`${errorLabel} 준비 중 오류가 발생했습니다. 다시 시도해주세요.`, `${errorLabel} 오류`);
            throw error;
        }
    }
    else {
        // ── originPrintLogic 경로 ──
        // 테이블 분할 없이 단일 요소를 통째로 캡처하여 PDF 생성
        let popup = null;
        try {
            const thisElement = resolveElement(element);
            // 이미지 렌더링 품질 최적화
            const imgTags = thisElement.querySelectorAll("img");
            imgTags.forEach((img) => {
                img.style.imageRendering = "optimizeQuality";
            });
            // 사용자 제스처 컨텍스트 내에서 팝업을 미리 열어둠
            popup = openFullSizePopup();
            if (!popup) {
                showAlert("팝업이 차단되었습니다. 브라우저 설정에서 팝업을 허용해주세요.", "팝업 차단");
                return "팝업이 차단되었습니다.";
            }
            // 로딩 메시지 표시
            popup.document.write(`<html><body><p>${errorLabel} 준비 중...</p></body></html>`);
            // 이미지 렌더링 완료 대기 후 캡처
            await delay(IMAGE_RENDER_DELAY_MS);
            const canvas = await html2canvas(thisElement, {
                scale: HTML2CANVAS_SCALE,
            });
            const pdf = new jsPDF("p", "mm", "a4");
            const pdfWidth = 210;
            const pdfHeight = 297;
            const imgWidth = pdfWidth - 2 * marginLeftRight;
            sliceCanvasToPdf({
                pdf,
                canvas,
                imgWidth,
                pdfHeightMinusMargin: pdfHeight - 2 * marginTopBottom,
                marginLeftRight,
                marginTopBottom,
                addPageBeforeFirst: false,
            });
            finalizePdf(pdf, popup, mode, fileName);
            await delay(POST_PRINT_DELAY_MS);
        }
        catch (error) {
            console.error(`[${errorLabel} 오류]`, error);
            if (popup && !popup.closed)
                popup.close();
            showAlert(`${errorLabel} 준비 중 오류가 발생했습니다. 다시 시도해주세요.`, `${errorLabel} 오류`);
            throw error;
        }
    }
}
